import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useGetTransactionEntities,
  useDeleteTransactionEntity,
  useCreateTransactionEntity,
  TRANSACTION_ENTITIES_QUERY_KEY,
  type FindTransactionEntitiesBody,
  type CreateTransactionEntityData,
} from '../../api/transactions';
import { TransactionEntity } from '../../models/TransactionEntity';
import { TransactionEntityFetchFilter } from '../../models/TransactionEntityFetchFilter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseTransactionEntitiesOptions {
  initialLimit?: number;
}

interface SidebarState {
  isOpen: boolean;
  entityToEdit: TransactionEntity | null;
  isEditMode: boolean;
}

interface DeleteModalState {
  isOpen: boolean;
  entity: TransactionEntity | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const createDefaultFilter = (limit = 20): TransactionEntityFetchFilter => {
  const filter = new TransactionEntityFetchFilter();
  filter.limit = limit;
  filter.page = 1;
  return filter;
};

const filterToApiBody = (filter: TransactionEntityFetchFilter): FindTransactionEntitiesBody => {
  const body: FindTransactionEntitiesBody = {
    limit: filter.limit ?? 20,
    page: filter.page ?? 1,
  };
  if (filter.id !== undefined) body.id = filter.id;
  if (filter.name?.trim()) body.name = filter.name.trim();
  if (filter.category?.trim()) body.category = filter.category;
  if (filter.type?.trim()) body.type = filter.type;
  if (filter.linked_to_kpi !== undefined) body.linked_to_kpi = filter.linked_to_kpi;
  return body;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const useTransactionEntities = (options: UseTransactionEntitiesOptions = {}) => {
  const { initialLimit = 20 } = options;
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // Filter state
  // ---------------------------------------------------------------------------
  const [filter, setFilter] = useState<TransactionEntityFetchFilter>(() =>
    createDefaultFilter(initialLimit)
  );

  // Build API body from filter
  const findFilters = useMemo(() => filterToApiBody(filter), [
    filter.id,
    filter.name,
    filter.category,
    filter.type,
    filter.linked_to_kpi,
    filter.limit,
    filter.page,
  ]);

  // ---------------------------------------------------------------------------
  // Sidebar state (create / edit)
  // ---------------------------------------------------------------------------
  const [sidebarState, setSidebarState] = useState<SidebarState>({
    isOpen: false,
    entityToEdit: null,
    isEditMode: false,
  });

  // ---------------------------------------------------------------------------
  // Delete modal state
  // ---------------------------------------------------------------------------
  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    isOpen: false,
    entity: null,
  });

  // ---------------------------------------------------------------------------
  // API Hooks
  // ---------------------------------------------------------------------------
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetTransactionEntities({ filters: findFilters });

  const createEntityMutation = useCreateTransactionEntity();
  const deleteEntityMutation = useDeleteTransactionEntity();

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const entities = useMemo(() => data?.entities ?? [], [data?.entities]);
  const pagination = useMemo(() => data?.pagination, [data?.pagination]);

  // ---------------------------------------------------------------------------
  // Filter handlers
  // ---------------------------------------------------------------------------
  const updateFilter = useCallback((updates: Partial<TransactionEntityFetchFilter>) => {
    setFilter((prev) => {
      const next = Object.create(Object.getPrototypeOf(prev));
      Object.assign(next, prev, updates);
      return next as TransactionEntityFetchFilter;
    });
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof TransactionEntityFetchFilter, value: unknown) => {
      updateFilter({ [key]: value, page: key !== 'page' ? 1 : (value as number) });
    },
    [updateFilter]
  );

  const handleSearchFilter = useCallback(
    (name: string) => {
      updateFilter({ name: name.trim() || undefined, page: 1 });
    },
    [updateFilter]
  );

  const handleCategoryFilter = useCallback(
    (category: string | undefined) => {
      updateFilter({ category: category || undefined, page: 1 });
    },
    [updateFilter]
  );

  const handleTypeFilter = useCallback(
    (type: string | undefined) => {
      updateFilter({ type: type || undefined, page: 1 });
    },
    [updateFilter]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      updateFilter({ page });
    },
    [updateFilter]
  );

  const resetFilters = useCallback(() => {
    setFilter(createDefaultFilter(initialLimit));
  }, [initialLimit]);

  // ---------------------------------------------------------------------------
  // Sidebar handlers (create / edit)
  // ---------------------------------------------------------------------------
  const openSidebarForNew = useCallback(() => {
    setSidebarState({
      isOpen: true,
      entityToEdit: null,
      isEditMode: false,
    });
  }, []);

  const openSidebarForEdit = useCallback((entity: TransactionEntity) => {
    setSidebarState({
      isOpen: true,
      entityToEdit: entity,
      isEditMode: true,
    });
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarState({
      isOpen: false,
      entityToEdit: null,
      isEditMode: false,
    });
  }, []);

  const handleEntityCreatedOrUpdated = useCallback(
    (_entity: TransactionEntity) => {
      // Always reload the list after create or edit
      void queryClient.invalidateQueries({ queryKey: TRANSACTION_ENTITIES_QUERY_KEY });
      updateFilter({ page: 1 });
      void refetch();
      closeSidebar();
    },
    [queryClient, updateFilter, refetch, closeSidebar]
  );

  // ---------------------------------------------------------------------------
  // Delete modal handlers
  // ---------------------------------------------------------------------------
  const openDeleteModal = useCallback((entity: TransactionEntity) => {
    setDeleteModalState({ isOpen: true, entity });
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (!deleteEntityMutation.isPending) {
      setDeleteModalState({ isOpen: false, entity: null });
    }
  }, [deleteEntityMutation.isPending]);

  const handleConfirmDelete = useCallback(() => {
    const entityId = deleteModalState.entity?.id;
    if (entityId === undefined) return;

    deleteEntityMutation.mutate(
      { entityId },
      {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, entity: null });
        },
      }
    );
  }, [deleteEntityMutation, deleteModalState.entity?.id]);

  // ---------------------------------------------------------------------------
  // Entity click handler (for detail view if needed)
  // ---------------------------------------------------------------------------
  const [selectedEntity, setSelectedEntity] = useState<TransactionEntity | null>(null);

  const handleEntityClick = useCallback((entity: TransactionEntity) => {
    setSelectedEntity(entity);
  }, []);

  const clearSelectedEntity = useCallback(() => {
    setSelectedEntity(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    // Data
    entities,
    pagination,
    selectedEntity,

    // Loading & error
    isLoading,
    isDeleting: deleteEntityMutation.isPending,
    isCreating: createEntityMutation.isPending,
    error,

    // Filter
    filter,
    handleFilterChange,
    handleSearchFilter,
    handleCategoryFilter,
    handleTypeFilter,
    handlePageChange,
    resetFilters,

    // Sidebar (create / edit)
    sidebarState,
    openSidebarForNew,
    openSidebarForEdit,
    closeSidebar,
    handleEntityCreatedOrUpdated,

    // Delete modal
    deleteModalState,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDelete,

    // Entity selection (detail)
    handleEntityClick,
    clearSelectedEntity,

    // Refetch
    refetch,
  };
};

export default useTransactionEntities;
